import {
  ChangeDetectionStrategy,
  Component,
  contentChildren,
  effect,
  ElementRef,
  inject,
  input,
  model,
  untracked,
} from '@angular/core';
import { UiTabComponent } from './tab.component';
import { NgTemplateOutlet } from '@angular/common';

type UiTabsVariant = 'default' | 'icon';
let nextTabsId = 0;

@Component({
  selector: 'app-ui-tabs',
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
})
export class UiTabsComponent {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly tabsId = `app-ui-tabs-${nextTabsId++}`;

  readonly activeTab = model<string | null>(null);
  readonly stretch = input(false);
  readonly variant = input<UiTabsVariant>('default');
  readonly tabs = contentChildren(UiTabComponent);

  constructor() {
    // Select the first enabled tab whenever the projected tabs change and the active one is gone.
    effect(() => {
      this.tabs();
      untracked(() => this.ensureActiveTab());
    });
  }

  handleActiveTabChange(tabId: string | undefined): void {
    if (!tabId) return;
    if (this.activeTab() === tabId) return;
    this.activeTab.set(tabId);
  }

  onTabKeydown(event: KeyboardEvent, tab: UiTabComponent): void {
    const enabledTabs = this.tabs().filter(tab => !tab.disabled());
    const currentIndex = enabledTabs.indexOf(tab);
    if (currentIndex < 0) return;

    const targetTab = this.getKeyboardTargetTab(event.key, enabledTabs, currentIndex);
    if (!targetTab) return;

    event.preventDefault();
    this.handleActiveTabChange(targetTab.tabId());
    this.focusTab(targetTab);
  }

  triggerClass(tab: UiTabComponent): string {
    return ['app-ui-tabs__trigger', tab.triggerClass()].filter(Boolean).join(' ');
  }

  isActive(tab: UiTabComponent): boolean {
    return this.activeTab() === tab.tabId();
  }

  labelId(tab: UiTabComponent): string {
    return `${this.tabsId}-label-${tab.tabId()}`;
  }

  panelId(tab: UiTabComponent): string {
    return `${this.tabsId}-content-${tab.tabId()}`;
  }

  private ensureActiveTab(): void {
    const availableTabs = this.tabs().filter(tab => !tab.disabled());
    if (!availableTabs.length) return;

    const currentTabIsAvailable = availableTabs.some(tab => tab.tabId() === this.activeTab());
    if (currentTabIsAvailable) return;

    this.handleActiveTabChange(availableTabs[0]?.tabId());
  }

  private getKeyboardTargetTab(
    key: string,
    enabledTabs: UiTabComponent[],
    currentIndex: number,
  ): UiTabComponent | undefined {
    if (key === 'Home') return enabledTabs[0];
    if (key === 'End') return enabledTabs[enabledTabs.length - 1];
    if (key === 'ArrowRight') return enabledTabs[(currentIndex + 1) % enabledTabs.length];
    if (key === 'ArrowLeft') return enabledTabs[(currentIndex - 1 + enabledTabs.length) % enabledTabs.length];
    return undefined;
  }

  private focusTab(tab: UiTabComponent): void {
    const tabButton = this.elementRef.nativeElement.querySelector<HTMLButtonElement>(`#${this.labelId(tab)}`);
    tabButton?.focus();
  }
}
